// Progress and rewards. Everything here is a pure function of the practice log, so the same rules
// serve a visitor (log in localStorage) and a registered user (log on the server), and importing a
// visitor's practices into an account carries the whole progress along.

import { isSolid } from "./moveRating.ts";
import type { Verdict } from "./moveRating.ts";

/** One finished practice, as the log stores it (localStorage for a visitor, the server for an account). */
export interface PracticeLogEntry {
    id: string;
    /** Which tool it was played in. Missing means the study, which is where they all used to be. */
    tool?: string;
    slug: string;
    lineName: string;
    color?: string;
    startedAt?: string;              // ISO
    endedAt: string;                 // ISO
    /** How long it took. The server works it out; a local log has the two timestamps instead. */
    durationSeconds?: number;
    endReason?: string;
    /** "won" | "lost" | "drawn" for the tools that play a game; missing for the ones nobody wins. */
    outcome?: string;
    ownMoves?: number;
    ratedMoves: number;
    accuracy: number | null;
    depth?: number;
    bestCombo?: number;
    counts?: Partial<Record<Verdict, number>>;
    xp?: number;
}

const MOVE_XP: Record<Verdict, number> = { best: 12, good: 8, inaccuracy: 3, mistake: 1, blunder: 0 };
const COMBO_XP_STEP = 2;
const COMBO_XP_MAX = 10;
const COMPLETION_XP = 20;
const STAR_XP = 10;
const PERSONAL_BEST_XP = 25;
const NEW_LINE_XP = 15;
const FIRST_OF_DAY_XP = 20;

export const DAILY_GOAL = 3;
/** What the server stores at most for one practice, so a tab left open all night is not six hours. */
const MAX_PRACTICE_SECONDS = 2 * 60 * 60;
// A star needs accuracy AND enough moves to mean something: three perfect moves are not mastery.
const STAR_THRESHOLDS = [{ accuracy: 60, moves: 3 }, { accuracy: 80, moves: 4 }, { accuracy: 92, moves: 6 }];

export interface Belt {
    name: string;
    color: string;
    xp: number;
}

// The dojo: belts are the long-term goal. One practice is worth roughly 100-150 XP.
export const BELTS: Belt[] = [
    { name: "White belt", color: "#f5f5f5", xp: 0 },
    { name: "Yellow belt", color: "#fdd835", xp: 150 },
    { name: "Orange belt", color: "#fb8c00", xp: 450 },
    { name: "Green belt", color: "#43a047", xp: 900 },
    { name: "Blue belt", color: "#1e88e5", xp: 1600 },
    { name: "Purple belt", color: "#8e24aa", xp: 2600 },
    { name: "Brown belt", color: "#6d4c41", xp: 4000 },
    { name: "Black belt", color: "#1a1a1a", xp: 6000 },
];
const DAN_XP = 2500;

/** XP for one move. `combo` is the run of solid moves including this one. */
export function xpForMove(verdict: Verdict | null | undefined, combo = 0, { hinted = false } = {}): number {
    if (hinted || !verdict) return 0;
    const bonus = isSolid(verdict) ? Math.min(COMBO_XP_MAX, Math.max(0, combo - 1) * COMBO_XP_STEP) : 0;
    return (MOVE_XP[verdict] || 0) + bonus;
}

/**
 * How long a practice took, as far as its entry can say: the number the server stored, or the two
 * timestamps a log on the device has. Bounded the same way the server bounds it, and 0 when there is
 * nothing to go on — a practice of unknown length must not turn into hours of study.
 */
export function secondsOf(entry: PracticeLogEntry): number {
    const bounded = (seconds: number) => Math.min(MAX_PRACTICE_SECONDS, Math.round(seconds));
    if (typeof entry.durationSeconds === "number" && entry.durationSeconds > 0) {
        return bounded(entry.durationSeconds);
    }
    if (!entry.startedAt || !entry.endedAt) return 0;
    const seconds = (new Date(entry.endedAt).getTime() - new Date(entry.startedAt).getTime()) / 1000;
    return Number.isFinite(seconds) && seconds > 0 ? bounded(seconds) : 0;
}

export function starsFor(accuracy: number | null | undefined, ratedMoves: number): number {
    if (accuracy === null || accuracy === undefined) return 0;
    return STAR_THRESHOLDS.filter((t) => accuracy >= t.accuracy && ratedMoves >= t.moves).length;
}

export interface Bonus {
    code: string;
    label: string;
    xp: number;
}

/** Bonus XP awarded when a practice ends, itemised so the results screen can show where it came from. */
export function sessionBonuses({ accuracy, ratedMoves, isPersonalBest, isNewLine, isFirstOfDay }: {
    accuracy: number | null;
    ratedMoves: number;
    isPersonalBest?: boolean;
    isNewLine?: boolean;
    isFirstOfDay?: boolean;
}): Bonus[] {
    const stars = starsFor(accuracy, ratedMoves);
    const bonuses: Bonus[] = [{ code: "completed", label: "Practice completed", xp: COMPLETION_XP }];
    if (stars) bonuses.push({ code: "stars", label: `${stars} star${stars === 1 ? "" : "s"}`, xp: stars * STAR_XP });
    if (isPersonalBest) bonuses.push({ code: "personal_best", label: "New personal best", xp: PERSONAL_BEST_XP });
    if (isNewLine) bonuses.push({ code: "new_line", label: "New line explored", xp: NEW_LINE_XP });
    if (isFirstOfDay) bonuses.push({ code: "first_of_day", label: "First practice today", xp: FIRST_OF_DAY_XP });
    return bonuses;
}

export interface BeltProgress {
    index: number;
    name: string;
    color: string;
    xpInto: number;
    xpForNext: number;
    progressPct: number;
    nextName: string;
}

export function beltFor(totalXp: number | null | undefined): BeltProgress {
    const xp = Math.max(0, totalXp || 0);
    const top = BELTS[BELTS.length - 1]!;
    if (xp >= top.xp) {
        const dan = 1 + Math.floor((xp - top.xp) / DAN_XP);
        const floor = top.xp + (dan - 1) * DAN_XP;
        return {
            index: BELTS.length - 1, name: dan === 1 ? top.name : `${top.name}, ${ordinal(dan)} dan`, color: top.color,
            xpInto: xp - floor, xpForNext: DAN_XP, progressPct: ((xp - floor) / DAN_XP) * 100, nextName: `${ordinal(dan + 1)} dan`,
        };
    }
    const index = BELTS.findLastIndex((b) => xp >= b.xp);
    const current = BELTS[index]!;
    const next = BELTS[index + 1]!;
    return {
        index, name: current.name, color: current.color, xpInto: xp - current.xp, xpForNext: next.xp - current.xp,
        progressPct: ((xp - current.xp) / (next.xp - current.xp)) * 100, nextName: next.name,
    };
}

function ordinal(n: number): string {
    const suffix = n % 10 === 1 && n % 100 !== 11 ? "st" : n % 10 === 2 && n % 100 !== 12 ? "nd"
        : n % 10 === 3 && n % 100 !== 13 ? "rd" : "th";
    return `${n}${suffix}`;
}

/** Local calendar day of a timestamp, as YYYY-MM-DD. */
export function dayKey(date: string | number | Date): string {
    const d = new Date(date);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function shiftDay(key: string, days: number): string {
    const [y, m, d] = key.split("-").map(Number) as [number, number, number];
    return dayKey(new Date(y, m - 1, d + days));
}

const previousDay = (key: string) => shiftDay(key, -1);
const nextDay = (key: string) => shiftDay(key, 1);

export interface Streak {
    current: number;
    best: number;
    activeToday: boolean;
}

/** Days in a row with at least one practice. The current streak survives until the end of the next day. */
export function streakOf(sessions: PracticeLogEntry[], now: Date = new Date()): Streak {
    const days = new Set(sessions.map((s) => dayKey(s.endedAt)));
    if (!days.size) return { current: 0, best: 0, activeToday: false };

    const run = (from: string) => {
        let length = 0;
        for (let key = from; days.has(key); key = previousDay(key)) length++;
        return length;
    };
    const today = dayKey(now);
    const activeToday = days.has(today);
    const current = activeToday ? run(today) : run(previousDay(today));
    const best = Math.max(...[...days].map((key) => (days.has(nextDay(key)) ? 0 : run(key))));
    return { current, best, activeToday };
}

export interface LineProgress {
    name: string;
    plays: number;
    bestAccuracy: number | null;
    lastAccuracy: number | null;
    firstAccuracy?: number;
    stars: number;
    bestDepth: number;
    biggestImprovement: number;
}

/**
 * Which tool a medal belongs to. "any" is the house: the practices, the days in a row and the time
 * put in count wherever they happened, and one belt still spans everything.
 */
export type AchievementTool = "any" | "study" | "memory" | "play" | "blindfold" | "positions";

export interface Achievement {
    id: string;
    tool: AchievementTool;
    name: string;
    desc: string;
    earned: boolean;
}

/** How much of the progress one tool accounts for. */
export interface ToolProgress {
    tool: string;
    sessions: number;
    xp: number;
    /** Time spent in it. Two tools with the same number of sessions are not the same amount of work. */
    seconds: number;
    lastAt: string | null;
}

/** Everything the UI shows about progress, before the achievements are tested against it. */
export interface ProgressBase {
    sessions: PracticeLogEntry[];
    /** One entry per tool that has been used, most practiced first. The belt spans them all. */
    tools: ToolProgress[];
    completedCount: number;
    totalXp: number;
    belt: BeltProgress;
    bestCombo: number;
    lines: Record<string, LineProgress>;
    colors: Set<string>;
    totalStars: number;
    /** Time put in over every tool. */
    totalSeconds: number;
    streak: Streak;
    today: { practices: number; goal: number };
}

export interface Progress extends ProgressBase {
    achievements: Achievement[];
}

/** The tool a session was played in. One from before the tools existed was the study's. */
export const STUDY_TOOL = "study";

const toolOf = (session: PracticeLogEntry) => session.tool || STUDY_TOOL;
const inTool = (p: ProgressBase, tool: string) => p.sessions.filter((s) => toolOf(s) === tool);
const countIn = (p: ProgressBase, tool: string) => inTool(p, tool).length;
const wonIn = (p: ProgressBase, tool: string) => inTool(p, tool).filter((s) => s.outcome === "won");
/**
 * The sessions of one tool that were a particular kind of run.
 *
 * A tool names the end of its own sessions, and that name is the only thing that survives into the
 * log: the blindfold room plays games and walks pieces across the board, the memory room remembers
 * openings and positions nobody ever played, and a medal has to know which it is looking at.
 */
const endedIn = (p: ProgressBase, tool: string, reason: string) =>
    inTool(p, tool).filter((s) => s.endReason === reason);
/** The exercises of a room, which are not games of it: a game is named by how it ended. */
const EXERCISES = ["journey", "squares"];
const gamesIn = (p: ProgressBase, tool: string) =>
    inTool(p, tool).filter((s) => !EXERCISES.includes(s.endReason || ""));
const HOUR = 3600;

/**
 * The medals. Each one says which tool it belongs to, because a room full of machines gives out its
 * own prizes: the ones of the study are about lines and accuracy, the ones of a game are about
 * winning it, and neither means anything in the other. What is the house's — the practices done, the
 * days in a row, the hours put in — is "any".
 */
const ACHIEVEMENTS: { id: string; tool: AchievementTool; name: string; desc: string; test: (p: ProgressBase) => boolean }[] = [
    // The house
    { id: "first_steps", tool: "any", name: "First Steps", desc: "Complete your first practice.", test: (p) => p.completedCount >= 1 },
    { id: "regular", tool: "any", name: "Regular", desc: "Complete 10 practices.", test: (p) => p.completedCount >= 10 },
    { id: "dedicated", tool: "any", name: "Dedicated", desc: "Complete 50 practices.", test: (p) => p.completedCount >= 50 },
    { id: "centurion", tool: "any", name: "Centurion", desc: "Complete 100 practices.", test: (p) => p.completedCount >= 100 },
    { id: "all_rounder", tool: "any", name: "All Rounder", desc: "Train in four different tools.", test: (p) => p.tools.length >= 4 },
    { id: "an_hour", tool: "any", name: "An Hour In", desc: "Spend an hour training.", test: (p) => p.totalSeconds >= HOUR },
    { id: "ten_hours", tool: "any", name: "Ten Hours In", desc: "Spend ten hours training.", test: (p) => p.totalSeconds >= 10 * HOUR },
    { id: "streak_3", tool: "any", name: "Three in a Row", desc: "Practice 3 days in a row.", test: (p) => p.streak.best >= 3 },
    { id: "streak_7", tool: "any", name: "Week Warrior", desc: "Practice 7 days in a row.", test: (p) => p.streak.best >= 7 },
    { id: "streak_30", tool: "any", name: "Iron Discipline", desc: "Practice 30 days in a row.", test: (p) => p.streak.best >= 30 },

    // The study: a line, and what your moves in it were worth
    { id: "sharp", tool: "study", name: "Sharp", desc: "Score 90% accuracy or more in a practice.", test: (p) => inTool(p, STUDY_TOOL).some((s) => s.accuracy !== null && s.accuracy >= 90 && s.ratedMoves >= 5) },
    { id: "flawless", tool: "study", name: "Flawless", desc: "Score 100% accuracy over 6 moves or more.", test: (p) => inTool(p, STUDY_TOOL).some((s) => s.accuracy !== null && s.accuracy >= 100 && s.ratedMoves >= 6) },
    { id: "on_a_roll", tool: "study", name: "On a Roll", desc: "Play 5 solid moves in a row.", test: (p) => p.bestCombo >= 5 },
    { id: "unstoppable", tool: "study", name: "Unstoppable", desc: "Play 10 solid moves in a row.", test: (p) => p.bestCombo >= 10 },
    { id: "deep_diver", tool: "study", name: "Deep Diver", desc: "Stay in the book for 8 moves.", test: (p) => inTool(p, STUDY_TOOL).some((s) => (s.depth || 0) >= 8) },
    { id: "book_worm", tool: "study", name: "Book Worm", desc: "Stay in the book for 12 moves.", test: (p) => inTool(p, STUDY_TOOL).some((s) => (s.depth || 0) >= 12) },
    { id: "explorer", tool: "study", name: "Explorer", desc: "Practice 5 different lines.", test: (p) => Object.keys(p.lines).length >= 5 },
    { id: "globetrotter", tool: "study", name: "Globetrotter", desc: "Practice 15 different lines.", test: (p) => Object.keys(p.lines).length >= 15 },
    { id: "line_veteran", tool: "study", name: "Line Veteran", desc: "Practice the same line 5 times.", test: (p) => Object.values(p.lines).some((l) => l.plays >= 5) },
    { id: "both_sides", tool: "study", name: "Both Sides", desc: "Practice as White and as Black.", test: (p) => p.colors.has("white") && p.colors.has("black") },
    { id: "mastered", tool: "study", name: "Mastered", desc: "Earn 3 stars on a line.", test: (p) => Object.values(p.lines).some((l) => l.stars === 3) },
    { id: "star_collector", tool: "study", name: "Star Collector", desc: "Collect 10 stars.", test: (p) => p.totalStars >= 10 },
    { id: "constellation", tool: "study", name: "Constellation", desc: "Collect 30 stars.", test: (p) => p.totalStars >= 30 },
    { id: "comeback", tool: "study", name: "Comeback", desc: "Improve your score on a line by 15 points.", test: (p) => Object.values(p.lines).some((l) => l.biggestImprovement >= 15) },

    // Memory: the pieces went back, or the moves came back
    { id: "memory_first", tool: "memory", name: "Good Memory", desc: "Finish a memory game.", test: (p) => countIn(p, "memory") >= 1 },
    { id: "memory_ten", tool: "memory", name: "Photographic", desc: "Finish 10 memory games.", test: (p) => countIn(p, "memory") >= 10 },
    { id: "memory_perfect", tool: "memory", name: "Nothing Missed", desc: "Score 100 in a memory game.", test: (p) => inTool(p, "memory").some((s) => (s.accuracy || 0) >= 100) },
    { id: "memory_sharp", tool: "memory", name: "Steady Recall", desc: "Score 90 or more in three memory games.", test: (p) => inTool(p, "memory").filter((s) => (s.accuracy || 0) >= 90).length >= 3 },

    // Play: a whole game against the sparring partner
    { id: "play_first", tool: "play", name: "First Bell", desc: "Play a sparring game.", test: (p) => countIn(p, "play") >= 1 },
    { id: "play_ten", tool: "play", name: "Sparring Partner", desc: "Play 10 sparring games.", test: (p) => countIn(p, "play") >= 10 },
    { id: "play_win", tool: "play", name: "Hand Raised", desc: "Beat your sparring partner.", test: (p) => wonIn(p, "play").length >= 1 },
    { id: "play_mate", tool: "play", name: "Finisher", desc: "Win a sparring game by checkmate.", test: (p) => wonIn(p, "play").some((s) => s.endReason === "checkmate") },
    { id: "play_long", tool: "play", name: "The Long Haul", desc: "Play 30 moves of your own in one game.", test: (p) => inTool(p, "play").some((s) => (s.ownMoves || 0) >= 30) },

    // Blindfold: the same game, held in your head
    // Your positions: a moment of your own games, found, kept or put right (endReason is `<exercise>_<result>`)
    { id: "positions_first", tool: "positions", name: "Your Own Teacher", desc: "Find the move in one of your own positions.", test: (p) => endedIn(p, "positions", "find_solved").length >= 1 },
    { id: "positions_kept", tool: "positions", name: "Kept It This Time", desc: "Keep an advantage you once threw away.", test: (p) => endedIn(p, "positions", "convert_solved").length >= 1 },
    { id: "positions_opening", tool: "positions", name: "Fixed the Opening", desc: "Put right an opening of yours that went wrong.", test: (p) => endedIn(p, "positions", "opening_solved").length >= 1 },
    { id: "positions_ten", tool: "positions", name: "Learning From Yourself", desc: "Solve 10 of your own positions.", test: (p) => wonIn(p, "positions").length >= 10 },
    { id: "blind_first", tool: "blindfold", name: "Eyes Closed", desc: "Play a game without seeing the board.", test: (p) => gamesIn(p, "blindfold").length >= 1 },
    { id: "blind_five", tool: "blindfold", name: "In the Dark", desc: "Play 5 games blindfold.", test: (p) => gamesIn(p, "blindfold").length >= 5 },
    { id: "blind_win", tool: "blindfold", name: "Sight Unseen", desc: "Win a game blindfold.", test: (p) => wonIn(p, "blindfold").length >= 1 },
    { id: "blind_deep", tool: "blindfold", name: "Held the Board", desc: "Play 20 moves of your own blindfold.", test: (p) => inTool(p, "blindfold").some((s) => (s.ownMoves || 0) >= 20) },

    // Blindfold again, the short exercise: one piece walked across a board that is not there
    { id: "journey_first", tool: "blindfold", name: "Pathfinder", desc: "Walk a piece across a board you cannot see.", test: (p) => endedIn(p, "blindfold", "journey").length >= 1 },
    { id: "journey_ten", tool: "blindfold", name: "Course Knowledge", desc: "Finish 10 runs of \"move the piece\".", test: (p) => endedIn(p, "blindfold", "journey").length >= 10 },
    { id: "journey_par", tool: "blindfold", name: "Par for the Course", desc: "Walk three journeys in a row in par.", test: (p) => endedIn(p, "blindfold", "journey").some((s) => (s.accuracy || 0) >= 100) },
    { id: "squares_first", tool: "blindfold", name: "Light or Dark", desc: "Call the colour of the squares for a minute.", test: (p) => endedIn(p, "blindfold", "squares").length >= 1 },
    { id: "squares_clean", tool: "blindfold", name: "Both Colours", desc: "A minute of squares with 30 answered and none wrong.", test: (p) => endedIn(p, "blindfold", "squares").some((s) => (s.accuracy || 0) >= 100 && (s.depth || 0) >= 30) },
    { id: "squares_run", tool: "blindfold", name: "Twenty Straight", desc: "Twenty squares in a row without a miss.", test: (p) => endedIn(p, "blindfold", "squares").some((s) => (s.bestCombo || 0) >= 20) },

    // Memory again, with nothing to recognise: a position nobody ever played
    { id: "memory_nowhere", tool: "memory", name: "Out of Nowhere", desc: "Put back a position nobody has ever played.", test: (p) => endedIn(p, "memory", "from_nowhere").length >= 1 },
    { id: "memory_nowhere_perfect", tool: "memory", name: "Camera", desc: "Score 100 on positions nobody has ever played.", test: (p) => endedIn(p, "memory", "from_nowhere").some((s) => (s.accuracy || 0) >= 100) },
];

/** What each tool adds up to. A session without a tool is one of the study, from before the others. */
function toolsOf(sessions: PracticeLogEntry[]): ToolProgress[] {
    const byTool = new Map<string, ToolProgress>();
    sessions.forEach((session) => {
        const tool = toolOf(session);
        const entry = byTool.get(tool) || { tool, sessions: 0, xp: 0, seconds: 0, lastAt: null };
        entry.sessions++;
        entry.xp += session.xp || 0;
        entry.seconds += secondsOf(session);
        if (!entry.lastAt || session.endedAt > entry.lastAt) entry.lastAt = session.endedAt;
        byTool.set(tool, entry);
    });
    return [...byTool.values()].sort((a, b) => b.sessions - a.sessions);
}

/**
 * Everything the UI shows about progress, derived from the practice log.
 * Sessions are expected oldest first; only counted practices should be in the log.
 */
export function computeProgress(sessions: PracticeLogEntry[], now: Date = new Date()): Progress {
    const ordered = [...sessions].sort((a, b) => new Date(a.endedAt).getTime() - new Date(b.endedAt).getTime());
    const lines: Record<string, LineProgress> = {};
    const colors = new Set<string>();
    let totalXp = 0;
    let bestCombo = 0;

    ordered.forEach((s) => {
        totalXp += s.xp || 0;
        bestCombo = Math.max(bestCombo, s.bestCombo || 0);
        // Only the study has lines. A game against the sparring partner or a run of a memory game
        // carries a name because everything stored does, not because it is a line to earn stars on.
        if (toolOf(s) !== STUDY_TOOL) return;
        if (s.color) colors.add(s.color);
        const line = lines[s.slug] || (lines[s.slug] = {
            name: s.lineName, plays: 0, bestAccuracy: null, lastAccuracy: null, stars: 0, bestDepth: 0, biggestImprovement: 0,
        });
        line.plays++;
        line.bestDepth = Math.max(line.bestDepth, s.depth || 0);
        if (s.accuracy !== null && s.accuracy !== undefined) {
            if (line.bestAccuracy !== null && s.accuracy > line.bestAccuracy) {
                // firstAccuracy is set below on the first rated practice, which is also the one that
                // leaves bestAccuracy non-null, so it exists by the time this branch can run.
                line.biggestImprovement = Math.max(line.biggestImprovement, s.accuracy - line.firstAccuracy!);
            }
            if (line.firstAccuracy === undefined) line.firstAccuracy = s.accuracy;
            line.bestAccuracy = line.bestAccuracy === null ? s.accuracy : Math.max(line.bestAccuracy, s.accuracy);
            line.lastAccuracy = s.accuracy;
            line.stars = Math.max(line.stars, starsFor(s.accuracy, s.ratedMoves));
        }
    });

    const today = dayKey(now);
    const base: ProgressBase = {
        sessions: ordered, tools: toolsOf(ordered),
        completedCount: ordered.length, totalXp, belt: beltFor(totalXp), bestCombo, lines, colors,
        totalStars: Object.values(lines).reduce((sum, l) => sum + l.stars, 0),
        totalSeconds: ordered.reduce((sum, s) => sum + secondsOf(s), 0),
        streak: streakOf(ordered, now),
        today: { practices: ordered.filter((s) => dayKey(s.endedAt) === today).length, goal: DAILY_GOAL },
    };
    return {
        ...base,
        achievements: ACHIEVEMENTS.map((a) => ({
            id: a.id, tool: a.tool, name: a.name, desc: a.desc, earned: a.test(base),
        })),
    };
}

export interface Rewards {
    achievements: Achievement[];
    beltUp: BeltProgress | null;
    streakExtended: boolean;
}

/** What a finished practice unlocked, comparing progress before and after it. */
export function rewardsOf(before: Progress, after: Progress): Rewards {
    const earnedBefore = new Set(before.achievements.filter((a) => a.earned).map((a) => a.id));
    return {
        achievements: after.achievements.filter((a) => a.earned && !earnedBefore.has(a.id)),
        beltUp: after.belt.name !== before.belt.name ? after.belt : null,
        streakExtended: after.streak.current > before.streak.current,
    };
}

/** Context flags for sessionBonuses, from the log as it was before the practice that just ended. */
export function bonusContext(before: Progress, { slug, accuracy }: { slug: string; accuracy: number | null }) {
    const line = before.lines[slug];
    return {
        isNewLine: !line,
        isPersonalBest: !!line && line.bestAccuracy !== null && accuracy !== null && accuracy > line.bestAccuracy,
        isFirstOfDay: before.today.practices === 0,
    };
}
